import Application from "../../Application";
import GetAllOrganisationsInteractor from "../../organisations/interactors/GetAllOrganisationsInteractor";
import SearchResultsPresenter, {
  ISearchResultsPresenter
} from "../presenter/SearchResultsPresenter";
import { TSearchQueryRequest } from "../service/SearchService";
import RunSimpleSearchQueryInteractor from "./RunSimpleSearchQueryInteractor";
import RunSearchQueryInteractor from "./RunSearchQueryInteractor";
import GetAllIndexTypeCategoriesInteractor from "../../indexTypes/interactors/GetAllIndexTypeCategoriesInteractor";
import GetHarmonizedIndexTreeInteractor from "../../organisations/interactors/GetHarmonizedIndexTreeInteractor";
import { GenericCodebookTypes } from "../../core/common/Constants";
import { SEARCH_SORT_FIELDS } from "../common/Constants";
import GetGenericCodebookInteractor from "../../codebooks/interactor/GetGenericCodebookInteractor";
import GetSelectedLanguageInteractor from "../../core/interactor/language/GetSelectedLanguageInteractor";
import { IUser } from "../../user/model/User";

export default class ShowSearchResultsInteractor {
  private application: Application;
  private output?: ISearchResultsPresenter;
  private runSimpleSearchQuery: RunSimpleSearchQueryInteractor;
  private runSearchQuery: RunSearchQueryInteractor;
  private getAllOrganisations: GetAllOrganisationsInteractor;
  private getHarmonizedIndexTree: GetHarmonizedIndexTreeInteractor;
  private getAllIndexTypeCategories: GetAllIndexTypeCategoriesInteractor;
  private getGenericCodebook: GetGenericCodebookInteractor;
  private getSelectedLanguage: GetSelectedLanguageInteractor;

  constructor({
    application,
    runSimpleSearchQuery,
    getAllOrganisations,
    getHarmonizedIndexTree,
    getAllIndexTypeCategories,
    runSearchQuery,
    getGenericCodebook,
    getSelectedLanguage
  }: any) {
    this.application = application;
    this.runSimpleSearchQuery = runSimpleSearchQuery;
    this.getAllOrganisations = getAllOrganisations;
    this.getHarmonizedIndexTree = getHarmonizedIndexTree;
    this.getAllIndexTypeCategories = getAllIndexTypeCategories;
    this.runSearchQuery = runSearchQuery;
    this.getGenericCodebook = getGenericCodebook;
    this.getSelectedLanguage = getSelectedLanguage;
  }

  private async getReferenceLists() {
    const indexCategories = await this.getAllIndexTypeCategories.execute();

    return Promise.all([
      this.getAllOrganisations.execute(),
      Promise.all(
        indexCategories.map((category) =>
          this.getHarmonizedIndexTree.execute(category.id)
        )
      ),
      this.getCurrentLanguageCode(),
      this.getGenericCodebook.execute(
        GenericCodebookTypes.COURT_CASE_TYPE,
        false
      ),
      this.getGenericCodebook.execute(GenericCodebookTypes.DECISION_TYPE, false)
    ]);
  }

  private async getCurrentLanguageCode() {
    const currentLanguage = await this.getSelectedLanguage.execute();
    return String(currentLanguage);
  }

  execute(query: TSearchQueryRequest) {
    const user: IUser = this.application.store.getState<any>().user;
    const isAuthorisedUser =
      user?.isOperator() || user?.isAdmin() || user?.isSuperAdmin() || false;
    this.output = SearchResultsPresenter({
      application: this.application,
      initialState: {
        searchQuery: query,
        sortFields: SEARCH_SORT_FIELDS,
        canCreateDocuments: isAuthorisedUser && !this.application.isPortal()
      }
    });
    let queryStrategy =
      query.type === "advanced"
        ? this.runSearchQuery
        : this.runSimpleSearchQuery;

    Promise.all([queryStrategy.execute(query), this.getReferenceLists()]).then(
      ([
        result,
        [
          organisations,
          indexTrees,
          currentLanguage,
          courtCaseTypes,
          caseDecisionTypes
        ]
      ]) => {
        this.output?.loadSearchResults(query, result);
        this.output?.loadReferenceValues(
          organisations,
          indexTrees,
          currentLanguage,
          courtCaseTypes,
          caseDecisionTypes
        );
      }
    );
    return this.output;
  }
}
